import { LoadStopTypes, NormalizedLoad, TMSLocation } from 'types/Load';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import {
  injectSelectedObject,
  normalizeExternalTMSID,
} from 'utils/loadInfoAndBuilding';

import { normalizeHoursTime } from './businessHours';

type StopFormData = NormalizedLoad['pickup'] | NormalizedLoad['consignee'];

// Quick Entry submits an ad-hoc stop, not a selected TMS location. Keep its name
// but omit the selected location's ID, street address, and timezone.
export function cleanStopDataForQuickEntry(stopData: StopFormData) {
  const {
    externalTMSID: _externalTMSID,
    addressLine1: _addressLine1,
    addressLine2: _addressLine2,
    timezone: _timezone,
    ...rest
  } = stopData;
  return rest;
}

export function findLocationByExternalTMSID(
  locations: Maybe<TMSLocation[]>,
  externalTMSID: MaybeUndef<string>
): TMSLocation | undefined {
  const id = normalizeExternalTMSID(externalTMSID);
  if (!id) {
    return undefined;
  }

  return locations?.find(
    (loc) => normalizeExternalTMSID(loc.externalTMSID) === id
  );
}

export function buildVirtualLocationFromStop(
  externalTMSID: string,
  stop: StopFormData
): TMSLocation | null {
  if (!stop?.name?.trim()) {
    return null;
  }

  return stopFormDataToTMSLocation(externalTMSID, stop);
}

/** Map current stop form values to a TMSLocation row for list merge/inject. */
export function stopFormDataToTMSLocation(
  externalTMSID: string,
  stop: StopFormData
): TMSLocation {
  return {
    ID: null,
    externalTMSID: normalizeExternalTMSID(externalTMSID),
    name: stop.name || '',
    addressLine1: stop.addressLine1 || '',
    addressLine2: stop.addressLine2 || '',
    city: stop.city || '',
    state: stop.state || '',
    zipCode: stop.zipCode || '',
    country: stop.country || '',
    contact: stop.contact || '',
    phone: stop.phone || '',
    email: stop.email || '',
    refNumber: stop.refNumber || '',
    instructions: stop.apptNote || '',
    apptRequired: stop.apptRequired ?? false,
  };
}

/** Pickup uses isPickupAptRequired; consignee uses isDeliveryAptRequired; fall back to apptRequired. */
export function apptRequiredFromTMSLocation(
  location: Pick<
    TMSLocation,
    'apptRequired' | 'isPickupAptRequired' | 'isDeliveryAptRequired'
  >,
  stopType: LoadStopTypes
): boolean {
  const fromStopType =
    stopType === LoadStopTypes.Consignee
      ? location.isDeliveryAptRequired
      : location.isPickupAptRequired;
  if (fromStopType != null) {
    return Boolean(fromStopType);
  }
  return Boolean(location.apptRequired);
}

/** True when the form has a TMS ID selected and that row exists in the hydrated list. */
export function isSelectedLocationResolved(
  locations: Maybe<TMSLocation[]>,
  externalTMSID: MaybeUndef<string>
): boolean {
  const id = normalizeExternalTMSID(externalTMSID);
  if (!id) {
    return false;
  }
  return findLocationByExternalTMSID(locations, id) != null;
}

export function mergeLocationsWithStop(
  locations: Maybe<TMSLocation[]>,
  stop: StopFormData | undefined
): Maybe<TMSLocation[]> {
  const id = normalizeExternalTMSID(stop?.externalTMSID);
  if (!id || !stop) {
    return locations;
  }

  return mergeOrReplaceLocationInList(
    locations,
    stopFormDataToTMSLocation(id, stop)
  );
}

/** Pickup uses location.instructions; consignee uses notes (delivery defaults), then instructions. */
export function instructionsFromTMSLocation(
  location: Pick<TMSLocation, 'instructions' | 'notes'>,
  stopType: LoadStopTypes
): string {
  if (stopType === LoadStopTypes.Consignee) {
    return (
      (hasNonEmptyString(location.notes) && String(location.notes).trim()) ||
      (hasNonEmptyString(location.instructions) &&
        String(location.instructions).trim()) ||
      ''
    );
  }
  return hasNonEmptyString(location.instructions)
    ? String(location.instructions).trim()
    : '';
}

/** Format open/close (HH:MM) into Revenova shippingReceivingHours style HH:MM-HH:MM. */
export function businessHoursFromTMSLocation(
  location: Pick<TMSLocation, 'openTime' | 'closeTime'>
): string {
  const open = normalizeHHMM(location.openTime);
  const close = normalizeHHMM(location.closeTime);
  if (!open || !close) {
    return '';
  }
  return `${open}-${close}`;
}

function normalizeHHMM(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const hh = String(value.getHours()).padStart(2, '0');
    const mm = String(value.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
  return normalizeHoursTime(String(value)) ?? '';
}

export function hasNonEmptyString(value: unknown): boolean {
  return value != null && String(value).trim() !== '';
}

export function hasUsableLocationFields(loc: TMSLocation): boolean {
  return !!(loc.name?.trim() || loc.addressLine1?.trim() || loc.city?.trim());
}

/** True when the TMS row has enough data to lock location entry fields. */
export function hasCompleteAddressFields(loc: TMSLocation): boolean {
  return !!(
    loc.addressLine1?.trim() &&
    loc.city?.trim() &&
    loc.state?.trim() &&
    loc.zipCode?.trim()
  );
}

const MERGEABLE_LOCATION_STRING_FIELDS = [
  'name',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'zipCode',
  'country',
  'contact',
  'phone',
  'email',
  'refNumber',
  'instructions',
  'notes',
] as const satisfies readonly (keyof TMSLocation)[];

/** Upgrade a sparse TMS row without wiping existing values with empty form strings. */
export function mergeSparseTMSLocation(
  existing: TMSLocation,
  incoming: TMSLocation
): TMSLocation {
  const merged: TMSLocation = { ...existing };

  for (const field of MERGEABLE_LOCATION_STRING_FIELDS) {
    const incomingValue = incoming[field];
    if (hasNonEmptyString(incomingValue)) {
      merged[field] = String(incomingValue).trim();
    }
  }

  const incomingId = normalizeExternalTMSID(incoming.externalTMSID);
  if (incomingId) {
    merged.externalTMSID = incomingId;
  }

  return merged;
}

function normalizedMergeableFieldValue(value: unknown): string {
  return hasNonEmptyString(value) ? String(value).trim() : '';
}

/** True when two rows carry the same mergeable location payload. */
export function areMergeableLocationFieldsEqual(
  a: TMSLocation,
  b: TMSLocation
): boolean {
  if (
    normalizeExternalTMSID(a.externalTMSID) !==
    normalizeExternalTMSID(b.externalTMSID)
  ) {
    return false;
  }

  return MERGEABLE_LOCATION_STRING_FIELDS.every(
    (field) =>
      normalizedMergeableFieldValue(a[field]) ===
      normalizedMergeableFieldValue(b[field])
  );
}

/** Inject or upgrade a sparse TMS row with richer stop/form data. */
export function mergeOrReplaceLocationInList(
  locations: Maybe<TMSLocation[]>,
  virtualLocation: TMSLocation
): TMSLocation[] {
  const id = normalizeExternalTMSID(virtualLocation.externalTMSID);
  const list = locations ?? [];

  if (!id) {
    const alreadyInjected = list.some(
      (loc) =>
        !normalizeExternalTMSID(loc.externalTMSID) &&
        loc.name === virtualLocation.name
    );
    return alreadyInjected ? list : [virtualLocation, ...list];
  }

  const existingIndex = list.findIndex(
    (loc) => normalizeExternalTMSID(loc.externalTMSID) === id
  );

  if (existingIndex === -1) {
    return injectSelectedObject(virtualLocation, list);
  }

  const existing = list[existingIndex];
  if (
    hasCompleteAddressFields(existing) ||
    !hasUsableLocationFields(virtualLocation)
  ) {
    return list;
  }

  const merged = mergeSparseTMSLocation(existing, virtualLocation);
  if (areMergeableLocationFieldsEqual(existing, merged)) {
    return list;
  }

  const next = [...list];
  next[existingIndex] = merged;
  return next;
}
